// @flow
import type { ChildrenArray, Element, Node } from 'react';
import React from 'react';
import * as ReactIs from 'react-is';

import { usePropsValidation } from './validation/runtime-checks/index.js';
import { types as t } from './validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from './validation/runtime-checks/index.js';
import { ColumnInternal, RowContext, RowInternal } from './grid.js';
import { css } from './emotion-import-helper.js';
import { productColor, illustrationColor } from './designSystem.js';
import { Language, English, Spanish } from './languages.js';

import { Text, Bold, Italics } from './Text.js';
import {
  AlgebraicWorking,
  AlgebraicWorkingStepIndicator,
} from './AlgebraicWorking.js';
import BoxAndWhiskerPlot from './BoxAndWhiskerPlot.js';
import Latex from './Latex.js';
import { LatexTextAtom } from './LatexTextAtom.js';
import { CartesianPlane } from './CartesianPlane.js';
import DraftImage from './DraftImage.js';
import Image from './Image.js';
import { List } from './List.js';
import { MultipleChoice } from './MultipleChoice.js';
import { NumberLine } from './NumberLine.js';
import { Parts, Part } from './Parts.js';
import { PseudoCodeBlock } from './PseudoCodeBlock.js';
import { StemAndLeafPlot } from './StemAndLeafPlot.js';
import TabularData from './TabularData.js';
import { TwoColumn } from './Columns.js';
import { TwoColumnProof } from './TwoColumnProof.js';
import UglyLegacyImage from './UglyLegacyImage.js';
import { TAKBenchmark } from './topic-info/TAKBenchmark.js';
import { InternalPathways } from './worksheet/InternalPathways.js';
import { ContentContext } from './JsxContent.js';
import { PerPartTasks } from './worksheet/PerPartTasks.js';

const semiBoldProximaNova = 600;

const QuestionsGroup = (props: {|
  title: string,
  children: ChildrenArray<Element<typeof Question>>,
  moveToNextPrintedPage?: boolean,
  ...CompilerInjectedProps,
|}) => {
  const { failedValidation, addErrors } = usePropsValidation(
    props,
    QuestionsGroup,
  );
  const { availableColumns } = React.useContext(RowContext);
  if (failedValidation) return null;
  const { title, children, moveToNextPrintedPage } = props;
  return (
    <section
      style={{
        breakBefore: moveToNextPrintedPage ? 'page' : undefined,
        display: 'block',
        position: 'relative',
      }}
      className={css`
        :not(:first-of-type) {
          padding-top: 32px;
          border-top: 1px solid #e5e6e8;
        }
        :not(:last-of-type) {
          padding-bottom: 6px;
        }
      `}
    >
      <RowInternal>
        <ColumnInternal colSpan={1} />
        <ColumnInternal colSpan={availableColumns - 1}>
          <header
            style={{
              display: 'block',
              lineHeight: 1.2,
              fontSize: '1.25em',
              fontWeight: semiBoldProximaNova,
              color: productColor.cloudburst,
              marginBottom: '1em',
            }}
          >
            {title}
          </header>
        </ColumnInternal>
      </RowInternal>
      <RowInternal>
        <ColumnInternal colSpan={1} />
        <ColumnInternal colSpan={availableColumns - 1}>
          <div style={{ padding: '16px 0' }}>
            <InternalPathways
              elementTree={children}
              performValidation={props._performValidation ?? false}
              questionsGroupChildrenLocation={props._sourceLocations?.children}
              addErrors={addErrors}
            />
          </div>
        </ColumnInternal>
      </RowInternal>
      {children}
    </section>
  );
};
QuestionsGroup.spec = {
  title: t.string,
  children: t.childrenArray(t.elementType(Question)),
  moveToNextPrintedPage: t.optional(t.boolean),
};

/**
 * Parameters 
 * @param queenslandIcon Describes question categorisation (Apply to Queensland)
 *  - Simple familiar - relationships and interactions are obvious and have few elements; all the information to solve the problem is identifiable
 *  - Simple complex - relationships and interactions have a number of elements; all the information to solve the problem is identifiable
 *  - Complex unfamiliar - relationships and interactions have a number of elements; all the information to solve the problem is not immediately identifiable
 * @param FLIcon (Apply to Florida)
 *  - Honors (HN) - indicates questions that only apply to the honors (or advanced) version of the course
    - Access Point (AP) - refers to the Access Point standards, which are alternate versions of the standards (or outcomes) for students with special learning needs, so questions with the AP icon are specifically covering those standards
 * @param VAIcon (Apply to Virginia)
    - SOL - is the acronym for the Virginia (VA) outcomes "Standards of Learning". A SOL icon indicates questions that are modeled after those SOL standardized test items.
 */
function Question(props: {|
  label: string,
  pathways?: Array<'easy' | 'medium' | 'hard'>,
  conversionMetadata?: string,
  calculatorProhibited?: boolean,
  children: Node,
  moveToNextPrintedPage?: boolean,
  queenslandIcon?: 'simpleFamiliar' | 'complexFamiliar' | 'complexUnfamiliar',
  FLIcon?: 'honors' | 'accessPoint',
  VAIcon?: 'SOL',
  linkedExamples?: Array<string>,
  aiFriendly?: boolean,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    Question,
  );
  const { worksheetChatConfig } = React.useContext(ContentContext);

  React.useEffect(() => {
    if (props._performValidation === false || failedValidation) return;
    if (props.linkedExamples === undefined) return;

    if (props.linkedExamples.length === 0) {
      addErrors([
        {
          message: 'At least one linked example must be specified',
          ...locations.linkedExamples,
        },
      ]);
      return;
    }

    for (const label of props.linkedExamples) {
      // Must be an integer followed optionally by a single letter
      // Eg. "3", "5a", "13", "17c"
      if (label.match(/^\d+[a-z]?$/) === null) {
        addErrors([
          {
            message: `Invalid example label used. Labels must be an integer, optionally followed by a letter. Eg. '3', '4a'`,
            ...locations.linkedExamples,
          },
        ]);
        // Bail out if we've found an error as we only want a single message
        return;
      }
    }
  });

  // Add-hoc validation for pathways prop to prevent a) empty list b) repeated values
  React.useEffect(() => {
    if (props._performValidation === false || failedValidation) return;
    if (props.pathways === undefined) return;

    if (props.pathways.length === 0) {
      addErrors([
        {
          message: 'At least one pathway must be specified',
          ...locations.pathways,
        },
      ]);
      return;
    }

    const uniquePathways = new Set(props.pathways);
    if (uniquePathways.size !== props.pathways.length) {
      addErrors([
        {
          message: 'Only specify one of each pathway',
          ...locations.pathways,
        },
      ]);
    }
  });

  // Add-hoc validation: only 1 <Parts> element allowed in root of aiFriendly Questions
  React.useEffect(() => {
    if (props._performValidation === false || failedValidation) return;
    if (props.aiFriendly === undefined || props.aiFriendly === false) return;

    const childrenCopy = React.Children.toArray(children);
    const partsElements = childrenCopy.filter(
      (node) => ReactIs.isElement(node) && node.type === Parts,
    );

    if (partsElements.length > 1) {
      addErrors([
        {
          message: 'Only one <Parts> element is allowed as a child of Question',
          ...locations.children,
        },
      ]);
      return;
    }
  });

  // Add-hoc validation:
  // - only 1 <PerPartTasks> per Question
  // - <PerPartTasks> requires also using a <Parts> element in the Question
  // - <PerPartTasks> must precede <Parts> element(s)
  React.useEffect(() => {
    if (props._performValidation === false || failedValidation) return;
    const childrenCopy = React.Children.toArray(children);
    const perTaskPartsAndPartsElements = childrenCopy.filter(
      (node) =>
        ReactIs.isElement(node) &&
        (node.type === Parts || node.type === PerPartTasks),
    );
    const perPartTasksElements = perTaskPartsAndPartsElements.filter(
      (node) => node.type === PerPartTasks,
    );
    const partsElements = perTaskPartsAndPartsElements.filter(
      (node) => node.type === Parts,
    );

    if (perPartTasksElements.length > 1) {
      addErrors([
        {
          message: 'Only one <PerPartTasks> element is allowed per Question',
          ...locations.children,
        },
      ]);
      return;
    }

    // Futher checks should only be performed if we have a 1 <PerPartTasks> element
    if (perPartTasksElements.length !== 1) return;

    if (partsElements.length === 0) {
      addErrors([
        {
          message:
            '<PerPartTasks> element must be used in conjunction with <Parts>',
          ...locations.children,
        },
      ]);
      // Ensure subsequent logic has the invariant that a <Parts> element exists
      return;
    }

    const perPartTasksIndex = perTaskPartsAndPartsElements.findIndex(
      (node) => node.type === PerPartTasks,
    );
    const firstPartsIndex = perTaskPartsAndPartsElements.findIndex(
      (node) => node.type === Parts,
    );
    if (firstPartsIndex < perPartTasksIndex) {
      addErrors([
        {
          message:
            '<PerPartTasks> element must precede <Parts> element in Question',
          ...locations.children,
        },
      ]);
    }
  });

  if (failedValidation) return null;
  const {
    label,
    calculatorProhibited,
    children,
    moveToNextPrintedPage,
    queenslandIcon,
    FLIcon,
    VAIcon,
    linkedExamples,
  } = props;

  const superPartsIndex = getSuperPartsIndex(children);
  const clonedChildren = React.Children.map(children, (child, idx) => {
    // Not all children are React elements, so we musn't cloneElement on primitive types
    if (!ReactIs.isElement(child)) return child;
    return React.cloneElement(child, {
      _isSuperPartsHack: idx === superPartsIndex ? true : undefined,
      _aiFriendlyQuestion: props.aiFriendly,
    });
  });

  // TODO: Should fail to validate if both kinds of symbols are used, chose concatenation for now
  return (
    <RowInternal style={moveToNextPrintedPage ? { breakBefore: 'page' } : {}}>
      <ColumnInternal colSpan={1}>
        {calculatorProhibited && <CalculatorProhibitedIcon />}
        {queenslandIcon === 'simpleFamiliar' && (
          <QueenslandSimpleFamiliarIcon />
        )}
        {queenslandIcon === 'complexFamiliar' && (
          <QueenslandComplexFamiliarIcon />
        )}
        {queenslandIcon === 'complexUnfamiliar' && (
          <QueenslandComplexUnfamiliarIcon />
        )}
        {FLIcon === 'honors' && <FLHonorsIcon />}
        {FLIcon === 'accessPoint' && <FLAccessPointIcon />}
        {VAIcon === 'SOL' && <VASOLIcon />}
      </ColumnInternal>
      <ColumnInternal colSpan={1}>
        <div
          style={{
            color: productColor.lochmara,
            fontWeight: semiBoldProximaNova,
          }}
        >
          {label}
        </div>
      </ColumnInternal>
      <ColumnInternal colSpan={27}>
        <div style={{ marginBottom: 32 }}>
          <div>{clonedChildren}</div>
          <div
            style={{
              position: 'relative',
              marginTop: 8,
              minHeight: 34,
              display:
                linkedExamples !== undefined ||
                (worksheetChatConfig != null && props.aiFriendly)
                  ? 'block'
                  : 'none',
            }}
          >
            {linkedExamples !== undefined && (
              <LinkedExamplesPill examples={linkedExamples} />
            )}
            {worksheetChatConfig != null && props.aiFriendly && (
              <InternalWorksheetChatButton questionLabel={label} />
            )}
          </div>
        </div>
      </ColumnInternal>
      <ColumnInternal colSpan={1} />
    </RowInternal>
  );
}
Question.spec = {
  label: t.string,
  pathways: t.optional(t.arrayOf(t.unionOfValues('easy', 'medium', 'hard'))),
  conversionMetadata: t.optional(t.string),
  calculatorProhibited: t.optional(t.boolean),
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(AlgebraicWorking),
      t.elementType(AlgebraicWorkingStepIndicator),
      t.elementType(Bold),
      t.elementType(BoxAndWhiskerPlot),
      t.elementType(CartesianPlane),
      t.elementType(DraftImage),
      t.elementType(Image),
      t.elementType(Italics),
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
      t.elementType(List),
      t.elementType(MultipleChoice),
      t.elementType(NumberLine),
      t.elementType(Parts),
      t.elementType(PseudoCodeBlock),
      t.elementType(StemAndLeafPlot),
      t.elementType(TabularData),
      t.elementType(Text),
      t.elementType(TwoColumn),
      t.elementType(TwoColumnProof),
      t.elementType(UglyLegacyImage),
      t.elementType(TAKBenchmark),
      t.elementType(PerPartTasks),
    ),
  ),
  moveToNextPrintedPage: t.optional(t.boolean),
  queenslandIcon: t.optional(
    t.unionOfValues('simpleFamiliar', 'complexFamiliar', 'complexUnfamiliar'),
  ),
  FLIcon: t.optional(t.unionOfValues('honors', 'accessPoint')),
  VAIcon: t.optional(t.unionOfValues('SOL')),
  linkedExamples: t.optional(t.arrayOf(t.string)),
  aiFriendly: t.optional(t.boolean),
};

function getSuperPartsIndex(children: React$Node) {
  // prettier-ignore
  const romanNumerals = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x'];
  let superPartsIndex = null;
  const childrenCopy = React.Children.toArray(children);
  const partsElements = childrenCopy.filter(
    (node) => ReactIs.isElement(node) && node.type === Parts,
  );
  if (partsElements.length >= 2) {
    const maybeSuperParts = partsElements[0];
    const labels = React.Children.toArray(maybeSuperParts.props.children)
      .filter((node) => ReactIs.isElement(node) && node.type === Part)
      .map((part) => part.props.label);
    if (labels.every((label) => romanNumerals.includes(label))) {
      superPartsIndex = childrenCopy.indexOf(maybeSuperParts);
    }
  }
  return superPartsIndex;
}

function InternalWorksheetChatButton({
  questionLabel,
}: {
  questionLabel: string,
}) {
  const { worksheetChatConfig, _inEditorHostEnvironment } =
    React.useContext(ContentContext);

  // Won't occur in practice as we guaranteed this at the call site.
  if (worksheetChatConfig == null) return null;

  return (
    <a
      style={{
        // We use this positioning method to avoid flexbox due to printing issues
        position: 'absolute',
        right: 0,
        border: '1px solid #E5E6E8',
        fontSize: 14,
        borderRadius: 50,
        color: '#5D51AE',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 4,
        padding: '6px 12px',
        lineHeight: 'auto',
        fontWeight: 600,
        fontFamily: 'Proxima Nova',
        textDecoration: 'none',
      }}
      target="_blank"
      href={worksheetChatConfig.getUrl(questionLabel)}
      onClick={(event) => {
        if (_inEditorHostEnvironment) {
          event.preventDefault();
          alert('Launching worksheet chat is not supported in editor');
        } else {
          worksheetChatConfig.onClick(event);
        }
      }}
    >
      Get help from Milo <OpenInNewTabIcon />
    </a>
  );
}

function OpenInNewTabIcon() {
  return (
    <svg
      style={{ display: 'block' }}
      height="20px"
      viewBox="0 -960 960 960"
      width="20px"
      fill="#5D51AE"
    >
      <path d="M216-144q-29.7 0-50.85-21.15Q144-186.3 144-216v-528q0-29.7 21.15-50.85Q186.3-816 216-816h264v72H216v528h528v-264h72v264q0 29.7-21.15 50.85Q773.7-144 744-144H216Zm171-192-51-51 357-357H576v-72h240v240h-72v-117L387-336Z" />
    </svg>
  );
}

function LinkedExamplesPill({ examples }: { examples: Array<string> }) {
  // Validation prevents this case, but we'll be defensive just in case
  if (examples.length === 0) return null;

  const examplesListText = examples.join(', ');
  return (
    <div
      style={{
        fontFamily: 'Proxima Nova',
        fontWeight: 600,
        fontSize: 14,
        color: productColor.white,
        backgroundColor: illustrationColor.niagra,
        padding: '0px 12px',
        height: 24,
        borderRadius: 12,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <svg width={14} height={12}>
        <path
          d="M11.4 11.5999C10.9445 11.5999 10.5445 11.4666 10.2 11.1999C9.85544 10.9332 9.62211 10.5999 9.49998 10.1999H5.59998C4.98878 10.1999 4.46931 9.98604 4.04158 9.5583C3.61384 9.13057 3.39998 8.6111 3.39998 7.9999C3.39998 7.3887 3.61384 6.86924 4.04158 6.4415C4.46931 6.01377 4.98878 5.7999 5.59998 5.7999H7.59998C7.98878 5.7999 8.31944 5.6639 8.59198 5.3919C8.86398 5.11937 8.99998 4.7887 8.99998 4.3999C8.99998 4.0111 8.86398 3.68044 8.59198 3.4079C8.31944 3.1359 7.98878 2.9999 7.59998 2.9999H4.49998C4.37784 3.3999 4.14451 3.73324 3.79998 3.9999C3.45544 4.26657 3.05544 4.3999 2.59998 4.3999C2.04424 4.3999 1.57198 4.2055 1.18318 3.8167C0.794376 3.4279 0.599976 2.95564 0.599976 2.3999C0.599976 1.84417 0.794376 1.3719 1.18318 0.983102C1.57198 0.594302 2.04424 0.399902 2.59998 0.399902C3.05544 0.399902 3.45544 0.533236 3.79998 0.799902C4.14451 1.06657 4.37784 1.3999 4.49998 1.7999H7.59998C8.32211 1.7999 8.93598 2.0527 9.44158 2.5583C9.94718 3.0639 10.2 3.67777 10.2 4.3999C10.2 5.12204 9.94718 5.7359 9.44158 6.2415C8.93598 6.7471 8.32211 6.9999 7.59998 6.9999H5.59998C5.32211 6.9999 5.08611 7.09724 4.89198 7.2919C4.69731 7.48604 4.59998 7.72204 4.59998 7.9999C4.59998 8.27777 4.69731 8.51377 4.89198 8.7079C5.08611 8.90257 5.32211 8.9999 5.59998 8.9999H9.49998C9.62211 8.5999 9.85544 8.26657 10.2 7.9999C10.5445 7.73324 10.9445 7.5999 11.4 7.5999C11.9557 7.5999 12.428 7.7943 12.8168 8.1831C13.2056 8.5719 13.4 9.04417 13.4 9.5999C13.4 10.1556 13.2056 10.6279 12.8168 11.0167C12.428 11.4055 11.9557 11.5999 11.4 11.5999ZM2.59998 3.1999C2.82238 3.1999 3.01118 3.12204 3.16638 2.9663C3.32211 2.8111 3.39998 2.6223 3.39998 2.3999C3.39998 2.1775 3.32211 1.9887 3.16638 1.8335C3.01118 1.67777 2.82238 1.5999 2.59998 1.5999C2.37758 1.5999 2.18878 1.67777 2.03358 1.8335C1.87784 1.9887 1.79998 2.1775 1.79998 2.3999C1.79998 2.6223 1.87784 2.8111 2.03358 2.9663C2.18878 3.12204 2.37758 3.1999 2.59998 3.1999Z"
          fill={productColor.white}
        />
      </svg>
      <Language>
        <English>Example {examplesListText}</English>
        <Spanish>Ejemplo {examplesListText}</Spanish>
      </Language>
    </div>
  );
}

function CalculatorProhibitedIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="7" fill="#D5DDF9" />
      <path
        d="M9.56019 2.625H4.47585C3.98587 2.625 3.58725 3.01364 3.58725 3.49137V3.84686L4.80515 4.58645V3.84686C4.80515 3.70114 4.92632 3.583 5.07578 3.583H8.96026C9.10966 3.583 9.23083 3.70114 9.23083 3.84686V4.64276C9.23083 4.78848 9.10966 4.90658 8.96026 4.90658H5.33238L8.36411 6.74762V6.67343C8.36411 6.52772 8.48528 6.40958 8.63475 6.40958H9.0046C9.15406 6.40958 9.27516 6.52772 9.27516 6.67343V6.92342C9.27516 7.04579 9.18972 7.14875 9.07379 7.17856L10.4488 8.0135V3.49137C10.4488 3.01364 10.0502 2.625 9.56019 2.625Z"
        fill="#005894"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.58725 10.7644V6.63725L6.58463 8.45743V8.48483C6.58463 8.63054 6.7058 8.74868 6.85527 8.74868H7.06429L8.45898 9.59564C8.40088 9.64404 8.36411 9.71598 8.36411 9.79629V10.0463C8.36411 10.192 8.48528 10.3101 8.63475 10.3101H9.0046C9.13912 10.3101 9.25059 10.2144 9.27158 10.0891L10.4479 10.8034C10.4269 11.2631 10.0367 11.6308 9.56019 11.6308H4.47585C3.98587 11.6308 3.58725 11.2421 3.58725 10.7644ZM5.71627 10.0463C5.71627 10.192 5.59509 10.3101 5.44563 10.3101H5.07578C4.92639 10.3101 4.80522 10.192 4.80522 10.0463V9.79629C4.80522 9.65058 4.92639 9.53243 5.07578 9.53243H5.44563C5.59509 9.53243 5.71627 9.65058 5.71627 9.79629V10.0463ZM5.71627 8.48483C5.71627 8.63054 5.59509 8.74868 5.44563 8.74868H5.07578C4.92639 8.74868 4.80522 8.63054 4.80522 8.48483V8.23488C4.80522 8.08916 4.92639 7.97102 5.07578 7.97102H5.44563C5.59509 7.97102 5.71627 8.08916 5.71627 8.23488V8.48483ZM7.49575 10.0463C7.49575 10.192 7.37458 10.3101 7.22511 10.3101H6.85527C6.7058 10.3101 6.58463 10.192 6.58463 10.0463V9.79629C6.58463 9.65058 6.7058 9.53243 6.85527 9.53243H7.22511C7.37458 9.53243 7.49575 9.65058 7.49575 9.79629V10.0463Z"
        fill="#005894"
      />
      <path
        d="M3.3672 4.54205C3.14132 4.40488 2.847 4.4768 2.70983 4.70269C2.57266 4.92857 2.64458 5.22289 2.87047 5.36006L10.2798 9.85931C10.5057 9.99647 10.8 9.92455 10.9371 9.69867C11.0743 9.47278 11.0024 9.17847 10.7765 9.0413L3.3672 4.54205Z"
        fill="#005894"
      />
    </svg>
  );
}

function QueenslandSimpleFamiliarIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20ZM7.24195 13.445C5.96195 13.445 5.04195 13.015 4.41195 12.385L5.19195 11.305C5.66195 11.795 6.39195 12.205 7.31195 12.205C8.09195 12.205 8.47195 11.835 8.47195 11.455C8.47195 10.955 7.89195 10.785 7.12195 10.605L7.07691 10.5947C5.99379 10.3465 4.63195 10.0346 4.63195 8.565C4.63195 7.455 5.59195 6.555 7.16195 6.555C8.22195 6.555 9.10195 6.875 9.76195 7.485L8.97195 8.525C8.43195 8.025 7.71195 7.795 7.06195 7.795C6.42195 7.795 6.08195 8.075 6.08195 8.475C6.08195 8.925 6.64195 9.065 7.41195 9.245C8.51195 9.495 9.91195 9.825 9.91195 11.305C9.91195 12.525 9.04195 13.445 7.24195 13.445ZM12.288 13.325H10.868V6.655H15.588V7.875H12.288V9.325H15.518V10.545H12.288V13.325Z"
        fill="#195E8C"
      />
    </svg>
  );
}

function QueenslandComplexFamiliarIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2 1C1.44772 1 1 1.44772 1 2V18C1 18.5523 1.44772 19 2 19H18C18.5523 19 19 18.5523 19 18V2C19 1.44772 18.5523 1 18 1H2ZM10.3866 12.1694C9.90659 12.9994 9.06659 13.8094 7.54659 13.8094C5.59659 13.8094 4.04659 12.4094 4.04659 10.3594C4.04659 8.30936 5.59659 6.90936 7.54659 6.90936C9.06659 6.90936 9.91659 7.70936 10.3866 8.53936L9.16659 9.13936C8.88659 8.59936 8.28659 8.16936 7.54659 8.16936C6.38659 8.16936 5.50659 9.09936 5.50659 10.3594C5.50659 11.6194 6.38659 12.5494 7.54659 12.5494C8.28659 12.5494 8.88659 12.1294 9.16659 11.5794L10.3866 12.1694ZM12.6518 13.6894H11.2318V7.01936H15.9518V8.23936H12.6518V9.68936H15.8818V10.9094H12.6518V13.6894Z"
        fill="#195E8C"
      />
    </svg>
  );
}

function QueenslandComplexUnfamiliarIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.7081 0.292821C10.3176 -0.0976069 9.68453 -0.0976069 9.29403 0.292821L0.292968 9.29203C-0.0820308 9.68704 -0.0820312 10.3203 0.292968 10.7064L9.29403 19.7056C9.68453 20.096 10.3176 20.096 10.7081 19.7056L19.7092 10.7064C20.0938 10.3203 20.0938 9.68704 19.7092 9.29203L10.7081 0.292821ZM9.42509 11.8141C8.94369 12.6463 8.10125 13.4585 6.57683 13.4585C4.62117 13.4585 3.06666 12.0548 3.06666 9.99922C3.06666 7.94368 4.62117 6.5399 6.57683 6.5399C8.10125 6.5399 8.95372 7.34206 9.42509 8.1743L8.20154 8.77592C7.92073 8.23447 7.31898 7.8033 6.57683 7.8033C5.41346 7.8033 4.5309 8.73582 4.5309 9.99922C4.5309 11.2626 5.41346 12.1951 6.57683 12.1951C7.31898 12.1951 7.92073 11.774 8.20154 11.2225L9.42509 11.8141ZM13.2715 13.4585C11.2255 13.4585 10.2728 12.3155 10.2728 10.671V6.6502H11.717V10.6209C11.717 11.5534 12.2284 12.1951 13.2715 12.1951C14.2944 12.1951 14.8059 11.5534 14.8059 10.6209V6.6502H16.2501V10.661C16.2501 12.3255 15.2973 13.4585 13.2715 13.4585Z"
        fill="#195E8C"
      />
    </svg>
  );
}

function FLHonorsIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="9" cy="9" r="9" fill="#0875BE" />
      <path
        d="M6.76309 6V8.29714H5.01867V6H3.75V12H5.01867V9.61714H6.76309V12H8.03176V6H6.76309Z"
        fill="white"
      />
      <path
        d="M12.9813 6V9.25714L10.8405 6H9.88896V12H11.1576V8.74286L13.2985 12H14.25V6H12.9813Z"
        fill="white"
      />
    </svg>
  );
}

function FLAccessPointIcon() {
  return (
    <svg
      width="19"
      height="18"
      viewBox="0 0 19 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="9.75" cy="9" r="9" fill="#0875BE" />
      <path
        d="M8.69583 11.8125H10.0849L8.19578 6.1875H6.57657L4.6875 11.8125H6.07652L6.35433 10.9286H8.41802L8.69583 11.8125ZM6.73532 9.72321L7.38618 7.65804L8.03703 9.72321H6.73532Z"
        fill="white"
      />
      <path
        d="M12.8679 6.1875H10.7645V11.8125H12.0344V10.0446H12.8679C13.9632 10.0446 14.8125 9.18482 14.8125 8.11607C14.8125 7.04732 13.9632 6.1875 12.8679 6.1875ZM12.8679 8.83929H12.0344V7.39286H12.8679C13.2568 7.39286 13.5425 7.70625 13.5425 8.11607C13.5425 8.52589 13.2568 8.83929 12.8679 8.83929Z"
        fill="white"
      />
    </svg>
  );
}

function VASOLIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="20" height="20" rx="10" fill="#0875BE" />
      <path
        d="M4.245 13.626C2.922 13.626 2.076 12.996 1.725 12.069L2.967 11.349C3.192 11.871 3.579 12.222 4.29 12.222C4.974 12.222 5.145 11.952 5.145 11.709C5.145 11.322 4.785 11.169 3.84 10.908C2.904 10.647 1.986 10.197 1.986 8.991C1.986 7.776 3.012 7.074 4.101 7.074C5.136 7.074 5.946 7.569 6.405 8.496L5.19 9.207C4.974 8.757 4.677 8.478 4.101 8.478C3.651 8.478 3.426 8.703 3.426 8.955C3.426 9.243 3.579 9.441 4.56 9.738C5.514 10.026 6.585 10.359 6.585 11.691C6.585 12.906 5.613 13.626 4.245 13.626ZM10.4467 13.626C8.6377 13.626 7.1707 12.204 7.1707 10.35C7.1707 8.496 8.6377 7.074 10.4467 7.074C12.2557 7.074 13.7227 8.496 13.7227 10.35C13.7227 12.204 12.2557 13.626 10.4467 13.626ZM10.4467 12.222C11.4727 12.222 12.2827 11.475 12.2827 10.35C12.2827 9.225 11.4727 8.478 10.4467 8.478C9.4207 8.478 8.6107 9.225 8.6107 10.35C8.6107 11.475 9.4207 12.222 10.4467 12.222ZM16.1535 12.114H18.4035V13.5H14.7135V7.2H16.1535V12.114Z"
        fill="white"
      />
    </svg>
  );
}

export { QuestionsGroup, Question };
